// Local adapter for the custom bottom sheets (Accueil, Mon suivi, Réserver, Bibliothèque, Nutrition, Le studio, Séance, and
// the account sheet of Profil / Paramètres / Bilans / Messagerie / Onboarding). It keeps each sheet's own DOM and classes
// (backdrop div > sheet div > handle, title, content), so pixels, padding, placement and content order are unchanged, and
// moves the modal mechanics onto the REAL UBM dialog engine (vendored `@ubm/ui/components/dialog/engine`, 3f79194):
// Root / Overlay / Content / Title. The engine's Content runs the shared `useOverlayAccessible` hook, the same stack as
// MediaViewer: focus enters the sheet, Tab cycles inside, Escape closes only the top layer, the background is inert and
// hidden from assistive tech, the document scroll is locked, and focus returns to the control that opened the sheet. No
// second focus stack, no portal (the sheet stays inside the phone frame), no new design-system component.
// Backdrop: closing stays on the backdrop CLICK, as before (the engine's pointer-down-outside close is declined so a press
// never closes on the way down and lets its click land on the page underneath). Drafts live in the callers' state and are
// untouched: the sheet closes only through the same `onClose` as before.
//
// Single motion owner (13/10). The sheet's position is ONE value, the CSS `translate` held by drawer.tsx (presented offset in a
// WeakMap): the entrance springs it from the sheet's height to 0, the handle drags it 1:1, and every close (handle tap or swipe,
// backdrop, Escape) springs it from the CURRENT presented offset, with the finger's velocity on a swipe, to the sheet's height;
// `onClose` (the caller's unmount) runs on the spring's own completion, not on a timer. A grab during the entrance or the close
// stops the spring where it is and the sheet follows the finger from there (a close is cancelled). No CSS transform keyframe
// on the sheet any more, so nothing fights the drag. Until unmount the sheet stays a mounted, open dialog: focus trap, inert
// background and the hidden dock are kept through the close. Reduced motion: no translation and no spring, a short opacity
// fade in and out, `onClose` on the fade's own end. The backdrop only fades (colour, data-closing), never moves.
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { flushSync } from "react-dom";
import { Content, Overlay, Root, Title } from "@ubm/ui/components/dialog/engine";
import { DrawerHandle, presentedY, reduced, setY, springTo, stopSpring } from "./drawer";
import { useShellModal } from "./shell-state";

export interface CustomSheetProps {
  title: string;
  /** The caller's close: unmounts the sheet. Called once the closing motion has completed. */
  onClose: () => void;
  overlayClassName: string;
  className: string;
  /** Escape veto, e.g. while a native fullscreen video owns Escape. */
  escapeAllowed?: () => boolean;
  children: ReactNode;
}

const FADE_MS = 120;

export function CustomSheet({ title, onClose, overlayClassName, className, escapeAllowed, children }: CustomSheetProps) {
  // Mounted = open: the shell hides its dock (hidden + inert) while any custom sheet is on screen (global shell lot 13/10).
  useShellModal(true);
  const sheetRef = useRef<HTMLDivElement>(null);
  const closingRef = useRef(false);
  const fadeRef = useRef<Animation | null>(null);
  const mounted = useRef(true);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [closing, setClosing] = useState(false);

  // Entrance: from the sheet's height (off screen) to 0, through the same spring as the handle; reduced motion: opacity only.
  useLayoutEffect(() => {
    mounted.current = true;
    const el = sheetRef.current;
    if (el) {
      if (reduced()) fadeRef.current = el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: FADE_MS, easing: "linear" });
      else { setY(el, el.offsetHeight); springTo(el, 0, 0); }
    }
    return () => { mounted.current = false; if (el) stopSpring(el); fadeRef.current?.cancel(); };
  }, []);

  // A caller may decline the close (e.g. Bilans while its viewer is open): `onClose` is committed synchronously, so if the sheet
  // is still mounted right after, it is brought back from where it is instead of being left off screen.
  const finish = () => {
    flushSync(() => onCloseRef.current());
    const el = sheetRef.current;
    if (!mounted.current || !el || !closingRef.current) return;
    closingRef.current = false; setClosing(false);
    fadeRef.current?.cancel(); fadeRef.current = null;
    if (!reduced()) springTo(el, 0, 0);
  };

  /** Every close path: from the presented offset (and the finger's velocity on a swipe) to the sheet's height, then `onClose`. */
  const dismiss = (velocity = 0) => {
    const el = sheetRef.current;
    if (!el) { onCloseRef.current(); return; }
    if (closingRef.current) return;
    closingRef.current = true; setClosing(true);
    if (reduced()) {
      // Snapshot the PRESENTED opacity as a primitive string BEFORE cancelling a running fade (cancel() resets it to 1: a flash
      // when closing mid-entry), then fade out from that snapshot.
      const from = String(getComputedStyle(el).opacity);
      stopSpring(el); fadeRef.current?.cancel();
      const a = el.animate([{ opacity: from }, { opacity: 0 }], { duration: FADE_MS, easing: "linear", fill: "forwards" });
      fadeRef.current = a;
      a.onfinish = () => { if (closingRef.current && fadeRef.current === a) finish(); };
      return;
    }
    springTo(el, Math.max(el.offsetHeight, presentedY(el)), velocity, () => { if (closingRef.current) finish(); });
  };

  /** A pointer took the sheet: a close in flight is cancelled where it stands (the handle continues from the presented offset). */
  const grab = () => {
    if (!closingRef.current) return;
    closingRef.current = false; setClosing(false);
    if (fadeRef.current) { fadeRef.current.cancel(); fadeRef.current = null; }
  };

  return <Root open onOpenChange={open => { if (!open) dismiss(); }}>
    <Overlay className={overlayClassName} data-closing={closing || undefined} onClick={() => dismiss()}>
      <Content ref={sheetRef} className={className} onClick={e => e.stopPropagation()} onPointerDownOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => { if (escapeAllowed && !escapeAllowed()) e.preventDefault(); }}>
        <DrawerHandle onClose={() => dismiss()} onDismiss={dismiss} onGrab={grab} />
        <div className="cc-home-sheet-head"><Title>{title}</Title></div>
        {children}
      </Content>
    </Overlay>
  </Root>;
}
